import { baseService } from "../base";
import { 
  PayrollInvoice, 
  CreatePayrollInvoiceDto, 
} from "~/types/payroll";
import { QuickBooksInvoice } from "../booking/booking.api";

export interface PayrollInvoiceDetail {
  id: string;
  invoiceNumber: string;
  staffId: string;
  staff: {
    id: string;
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  periodStart: string;
  periodEnd: string;
  periodType: string;
  regularHours: number;
  overtimeHours: number;
  regularRate: number;
  overtimeRate: number;
  regularPay: number;
  overtimePay: number;
  bonuses: number;
  deductions: number;
  grossPay: number;
  netPay: number;
  status: string;
  notes?: string;
  metadata?: {
    quickBooksInvoiceId?: string;
    quickBooksDocNumber?: string;
    payrollType?: string;
    staffName?: string;
  };
  workSessions?: any[];
  quickBooksInvoice?: any;
  createdAt: string;
  updatedAt: string;
}

export const payrollInvoiceService = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    staffId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<{
    data: PayrollInvoice[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> => {
    const { data } = await baseService.get("/payroll", { params });
    return data;
  },

  getById: async (id: string): Promise<PayrollInvoice> => {
    const { data } = await baseService.get<PayrollInvoice>(`/payroll/${id}`);
    return data;
  },

  getDetail: async (id: string): Promise<PayrollInvoiceDetail> => {
    const { data } = await baseService.get<PayrollInvoiceDetail>(`/payroll/${id}/detail`);
    return data;
  },

  create: async (dto: CreatePayrollInvoiceDto): Promise<PayrollInvoice> => {
    const { data } = await baseService.post<PayrollInvoice>("/payroll", dto);
    return data;
  },

  update: async (id: string, dto: Partial<CreatePayrollInvoiceDto>): Promise<PayrollInvoice> => {
    const { data } = await baseService.put<PayrollInvoice>(`/payroll/${id}`, dto);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await baseService.delete(`/payroll/${id}`);
  },

  markAsPaid: async (id: string): Promise<PayrollInvoice> => {
    const { data } = await baseService.post<PayrollInvoice>(`/payroll/${id}/mark-as-paid`);
    return data;
  },

  getDashboardData: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<{
    totalDraft: number;
    totalPending: number;
    totalApproved: number;
    totalPaid: number;
    totalAmount: number;
    recentInvoices: PayrollInvoice[];
  }> => {
    const { data } = await baseService.get("/payroll/dashboard", { params });
    return data;
  },

  createWithQuickBooks: async (dto: CreatePayrollInvoiceDto): Promise<{
    success: boolean;
    message: string;
    data: PayrollInvoice;
    notification: {
      type: string;
      title: string;
      message: string;
      quickBooksInvoiceId?: string;
      quickBooksDocNumber?: string;
    };
  }> => {
    const { data } = await baseService.post("/payroll/create-and-approve", dto);
    return data;
  },

  createQuickBooksInvoice: async (payrollInvoiceId: string, lineItems: any[]): Promise<QuickBooksInvoice> => {
    const { data } = await baseService.post(`/api/quickbooks/payroll/${payrollInvoiceId}/create-invoice`, {
      payrollInvoiceId,
      lineItems,
    });
    return data;
  },

  getQuickBooksInvoices: async (payrollInvoiceId: string): Promise<QuickBooksInvoice[]> => {
    const { data } = await baseService.get(`/api/quickbooks/payroll/${payrollInvoiceId}/invoices`);
    return data;
  },

  syncQuickBooksInvoice: async (invoiceId: string): Promise<QuickBooksInvoice> => {
    const { data } = await baseService.post(`/api/quickbooks/invoices/${invoiceId}/sync`);
    return data;
  },

  // Work Sessions
  getWorkSessions: async (params?: {
    page?: number;
    limit?: number;
    staffId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    data: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> => {
    const { data } = await baseService.get("/payroll/work-sessions", { params });
    return data;
  },

  updateWorkSession: async (id: string, body: {
    clockIn?: string;
    clockOut?: string;
    notes?: string;
    unpaidBreakMinutes?: number;
  }): Promise<any> => {
    const { data } = await baseService.patch(`/payroll/work-sessions/${id}`, body);
    return data;
  },

  deleteWorkSession: async (id: string): Promise<void> => {
    await baseService.delete(`/payroll/work-sessions/${id}`);
  },
};